import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { useStepUp } from '../auth/useStepUp';
import { useCanWrite } from '../auth/permissions';
import { fetchUserQuotaGrants, grantQuota, revokeQuotaGrant } from './api';
import { QuotaGrantModal, type QuotaGrantResult } from './QuotaGrantModal';
import type { QuotaGrant } from './types';

// Kontingent-Karte im User-Detail (CUR-523).
//
// Zeigt den Bonus auch dann, wenn keiner existiert — die Karte ist zugleich die
// Antwort auf „wie viel hat dieser Nutzer eigentlich offen", die im Support-Fall
// regelmäßig gebraucht wird.

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function grantState(g: QuotaGrant): { label: string; muted: boolean } {
  if (g.revokedAt) return { label: 'zurückgenommen', muted: true };
  if (new Date(g.validUntil) <= new Date()) return { label: 'abgelaufen', muted: true };
  if (g.remaining === 0) return { label: 'ausgeschöpft', muted: true };
  return { label: `${g.remaining} offen`, muted: false };
}

export function QuotaGrantsCard({ userId }: { userId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  // Die Karte bleibt sichtbar — sie beantwortet „wie viel hat der Nutzer offen".
  // Nur Vergeben und Zuruecknehmen entfallen fuer die Leserolle (CUR-995).
  const canWrite = useCanWrite();
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['user-quota-grants', userId],
    queryFn: () => fetchUserQuotaGrants(userId),
  });

  const grantMutation = useMutation({
    mutationFn: async (result: QuotaGrantResult) => {
      const token = await stepUp.request({
        action: 'user.quotaGrant',
        description: `${result.amount} zusätzliche Tutor-Nachrichten bis ${formatDate(result.validUntil)}`,
      });
      return grantQuota({ userIds: [userId], feature: 'tutor', ...result }, token);
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['user-quota-grants', userId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const revokeMutation = useMutation({
    mutationFn: async (grant: QuotaGrant) => {
      const reason = window.prompt('Begründung für die Rücknahme:');
      if (!reason?.trim()) throw new Error('Rücknahme abgebrochen — Begründung ist Pflicht.');
      const token = await stepUp.request({
        action: 'user.quotaGrant',
        description: `Bonus über ${grant.amount} Nachrichten zurücknehmen`,
      });
      return revokeQuotaGrant(grant.id, reason.trim(), token);
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['user-quota-grants', userId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const grants = data?.grants ?? [];

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-3 flex items-center justify-between gap-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Kontingent (Text-Tutor)
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            disabled={grantMutation.isPending}
            className="rounded-md border px-3 py-1 text-xs text-text-primary disabled:opacity-50"
            style={{ borderColor: darkTheme.colors.borderSubtle }}
          >
            Kontingent erhöhen
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-text-secondary">lädt …</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-text-primary">
            Offener Bonus:{' '}
            <span className="font-semibold">
              {(data?.bonusRemaining ?? 0).toLocaleString('de-DE')} Nachrichten
            </span>
            {data?.bonusExpiresAt ? (
              <span className="text-text-secondary"> · nächster Ablauf {formatDate(data.bonusExpiresAt)}</span>
            ) : null}
          </p>

          {grants.length === 0 ? (
            <p className="text-sm text-text-secondary">Keine Grants vergeben.</p>
          ) : (
            <ul className="space-y-2">
              {grants.map((g) => {
                const state = grantState(g);
                return (
                  <li
                    key={g.id}
                    className="flex items-start justify-between gap-3 border-t pt-2 text-sm first:border-t-0 first:pt-0"
                    style={{ borderColor: darkTheme.colors.borderSubtle }}
                  >
                    <div className={state.muted ? 'text-text-tertiary' : 'text-text-primary'}>
                      <div>
                        {g.amount.toLocaleString('de-DE')} Nachrichten · {g.consumed} verbraucht ·{' '}
                        {state.label}
                      </div>
                      <div className="text-xs text-text-secondary">
                        bis {formatDate(g.validUntil)}
                        {g.campaign ? ` · Aktion „${g.campaign}“` : ''} · {g.reason}
                      </div>
                    </div>
                    {canWrite && !g.revokedAt && (
                      <button
                        type="button"
                        onClick={() => revokeMutation.mutate(g)}
                        disabled={revokeMutation.isPending}
                        className="shrink-0 text-xs text-text-secondary underline disabled:opacity-50"
                      >
                        zurücknehmen
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

      {showModal && (
        <QuotaGrantModal
          userCount={1}
          onCancel={() => setShowModal(false)}
          onSubmit={(result) => {
            setShowModal(false);
            grantMutation.mutate(result);
          }}
        />
      )}
      {stepUp.modal}
    </div>
  );
}
